// frontend/app/login/page.tsx

"use client";

import { useState, useEffect } from "react";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { Loader2 } from "lucide-react";
import { useTheme } from "next-themes";

// 시스템 진입을 위한 NextAuth 기반 로그인 페이지 컴포넌트
export default function LoginPage() {
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { setTheme } = useTheme();

  // 로그인 페이지 진입 시 무조건 라이트 모드로 강제 고정
  useEffect(() => {
    setTheme("light");
  }, [setTheme]);

  // 폼 제출 시 NextAuth Credentials 프로바이더를 통한 로그인 처리
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const res = await signIn("credentials", { 
        loginId, 
        password, 
        redirect: false 
      });

      if (res?.error) {
        setError(res.error);
      } else {
        router.push("/");
        router.refresh();
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-gray-50 dark:bg-[#111111]">
      <div className="w-full max-w-sm p-8 bg-white dark:bg-[#191919] rounded-2xl shadow-lg border border-gray-100 dark:border-zinc-800">
        
        {/* 서비스 로고 및 타이틀 영역 */}
        <div className="flex flex-col items-center mb-8">
          <img src="/Logo.png" alt="Company Logo" className="h-10 w-auto mb-4 object-contain" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">AlphaNote</h1>
          <p className="text-sm text-gray-500 mt-1">업무의 시작, 알파노트</p>
        </div>

        {/* 로그인 정보 입력 폼 */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              아이디 (사번)
            </label>
            <input
              type="text"
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
              autoComplete="username"
              className="w-full p-2.5 bg-gray-50 dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-lg outline-none focus:ring-2 focus:ring-[#006DBA] text-gray-900 dark:text-white transition-all"
              placeholder="아이디를 입력하세요"
              required
              disabled={isLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              비밀번호
            </label>
            <input
              type="password"
              // value prop을 의도적으로 생략(uncontrolled) — React가 controlled input(value prop)에는
              // 매 키 입력마다 node.defaultValue를 동기화하는데, defaultValue는 실제 HTML value 속성에
              // 반영되는 스펙 동작이라 F12 Elements 패널에 평문 비밀번호가 그대로 노출됨.
              // onChange만으로 상태를 읽으면 이 속성 동기화 자체가 발생하지 않음.
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full p-2.5 bg-gray-50 dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-lg outline-none focus:ring-2 focus:ring-[#006DBA] text-gray-900 dark:text-white transition-all"
              placeholder="비밀번호 입력"
              required
              disabled={isLoading}
            />
          </div>

          {/* 에러 메시지 출력 영역 */}
          {error && (
            <p className="text-red-500 text-sm text-center bg-red-50 p-2 rounded">
              {error}
            </p>
          )}

          {/* 제출 버튼 (로딩 상태 연동) */}
          <button 
            type="submit" 
            disabled={isLoading}
            className="w-full flex items-center justify-center py-2.5 bg-[#006DBA] hover:bg-[#005A9E] disabled:bg-[#006DBA]/70 text-white font-medium rounded-lg transition-colors mt-4"
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin mr-2" />
                로그인 중...
              </>
            ) : (
              "로그인"
            )}
          </button>
        </form>

        <div className="text-center mt-4">
          <a
            href="/request-password-reset"
            className="text-xs text-gray-400 hover:text-[#006DBA] dark:text-zinc-500 dark:hover:text-[#3399FF] transition-colors"
          >
            비밀번호를 잊으셨나요?
          </a>
        </div>

      </div>
    </div>
  );
}